"use client"

import { ZoomEffectButton } from "@/(admin)/components/button/ZoomEffectButton"
import { t } from "@/languages/i18n"
import { toImageSrc } from "@/lib/utils"
import { postWidgetRegistration } from "@/services/widget-registration-service"
import type { WidgetRegistrationItemProps } from "@/types/types"
import { isExternalLink } from "@/utils/ensure-protocol-helper"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { useWidgetRegistration } from "../hooks/useWidgetRegistration"

export const WidgetRegistrationItem = ({ item, className = "", isCMS = false }: WidgetRegistrationItemProps) => {
  const router = useRouter()
  const { siteId } = useWidgetRegistration({ items: [] })
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const imageUrl = isCMS ? toImageSrc(item.image) : item.image

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    if (hasAttemptedSubmit && emailError) {
      setEmailError("")
    }
  }

  const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (item.hasEmailInput) {
      setHasAttemptedSubmit(true)
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailError(t("Vui lòng nhập email hợp lệ"))
        return
      }
      setEmailError("")
      setIsLoading(true)

      try {
        await postWidgetRegistration({ email })

        setIsSubmitted(true)
      } catch (error) {
        setEmailError(t("Đã xảy ra lỗi khi gửi email. Vui lòng thử lại."))
      } finally {
        setIsLoading(false)
      }
      return
    }

    if (item.buttonType === "onClick" && item.buttonOnClick) {
      try {
        const func = new Function("email", item.buttonOnClick)
        func(email)
      } catch (error) {
        // Silent error
      }
    }
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!item.buttonLink || item.buttonLink === "#") return

    if (isExternalLink(item.buttonLink)) {
      window.open(item.buttonLink, "_blank", "noopener,noreferrer")
    } else {
      router.push(item.buttonLink)
    }
  }

  return (
    <div
      className={`widget-registration-item relative flex flex-col w-full sm:w-full h-auto sm:h-full sm:max-w-[634px] sm:max-h-[398px] bg-white rounded-lg shadow-md overflow-hidden ${className}`}
    >
      {item.image && (
        <div className="widget-registration-image absolute inset-0 z-0">
          <Image src={imageUrl} alt={item.title} fill className="object-cover" />
        </div>
      )}

      <div className="relative z-10 flex flex-col py-0 sm:pt-[2.75rem] sm:pb-[1.9375rem] px-0 items-center justify-start max-h-[250px] h-[250px] sm:min-h-[398px]">
        {item.title && (
          <h3 className="widget-registration-title text-lg sm:text-2xl font-extrabold leading-[1.575rem] sm:leading-[1.7rem] text-[#333333] bg-white pb-0 sm:pb-[1.25rem] rounded inline-block text-center mt-[1.375rem] sm:mt-0">
            {item.title}
          </h3>
        )}

        {item.description && (
          <p className="widget-registration-description text-sm text-[#333333] bg-white pb-0 leading-[1.225rem] sm:leading-[1.35625rem] rounded inline-block text-center px-[1.125rem] sm:px-0 mt-3 sm:mt-0">
            {item.description}
          </p>
        )}

        {item.hasEmailInput && (
          <div className="widget-registration-email w-full px-10 sm:max-w-[400px] mt-4 sm:mt-7">
            <div className="flex gap-0 mb-2 sm:w-[400px]">
              <input
                type="text"
                value={email}
                onChange={handleEmailChange}
                placeholder={t("Email")}
                disabled={isLoading}
                className={`flex-1 pl-[0.875rem] w-full sm:w-[317px] h-[42px] border border-[#CCCCCC] rounded rounded-r-none bg-white text-base placeholder:text-[#CCCCCC] placeholder:text-base placeholder:font-normal placeholder:leading-[2.5rem] ${emailError ? "border-red-500" : ""
                  } disabled:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-[#18AF4E] focus:border-[#18AF4E]`}
              />
              <ZoomEffectButton
                text={item.buttonText}
                onClick={handleOnClick}
                isInline={true}
                isLoading={isLoading}
                loadingText={t("Đang gửi...")}
              />
            </div>
            {emailError && (
              <p className="text-red-500 text-sm mt-1 bg-white px-2 py-1 rounded inline-block shadow-sm">
                {emailError}
              </p>
            )}
            {isSubmitted && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded shadow-sm w-full sm:w-auto">
                <p className="text-green-800 font-bold mb-1">{t("Cảm ơn!")}</p>
                <p className="text-[#333333] text-sm">{t("Thank you for subscribing to our newsletter")}</p>
              </div>
            )}
          </div>
        )}

        {!item.hasEmailInput && (
          <div className="widget-registration-button mt-7">
            {item.buttonType === "link" ? (
              <ZoomEffectButton text={item.buttonText} onClick={handleLinkClick} isInline={false} />
            ) : (
              <ZoomEffectButton
                text={item.buttonText}
                onClick={handleOnClick}
                isInline={false}
                isLoading={isLoading}
                loadingText={t("Đang gửi...")}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
